import { create } from "zustand";
import { persist } from "zustand/middleware";
import { getModuleDefs } from "./workspaces";

export interface GlobalTab {
	id: string;
	path: string;
	title: string;
	pinned?: boolean;
	icon?: string;
	dirty?: boolean;
	/** Module this tab belongs to (for visual grouping). */
	moduleId?: string;
}

const MAX_CLOSED = 20;
const MAX_TABS = 30;

interface TabState {
	tabs: GlobalTab[];
	activeTabId: string | null;
	/** Stack of recently closed tabs for Cmd+Shift+T reopen. */
	closedTabs: GlobalTab[];

	openTab: (tab: Omit<GlobalTab, "id">) => string;
	setActiveTab: (id: string) => void;
	closeTab: (id: string) => string | null;
	/** Close all tabs except the given one. */
	closeOthers: (id: string) => void;
	/** Close all tabs to the right of the given one. */
	closeRight: (id: string) => void;
	/** Reopen last closed tab. Returns path to navigate to, or null. */
	reopenTab: () => string | null;
	/** Reorder tabs via drag-and-drop. */
	reorderTabs: (fromIndex: number, toIndex: number) => void;
	setDirty: (id: string, dirty: boolean) => void;
	setTitle: (id: string, title: string) => void;
	togglePin: (id: string) => void;
	syncWithPath: (path: string, title: string) => void;
}

function tabId(path: string): string {
	return path.replace(/\/+$/, "") || "/";
}

function resolveModuleId(path: string): string | undefined {
	const defs = getModuleDefs();
	for (const m of defs) {
		if (path.startsWith(m.prefix)) return m.id;
	}
	if (path.startsWith("/settings")) return "settings";
	if (path.startsWith("/admin")) return "admin";
	return undefined;
}

export const useTabStore = create<TabState>()(
	persist(
		(set, get) => ({
			tabs: [],
			activeTabId: null,
			closedTabs: [],

			openTab: (tab) => {
				const id = tabId(tab.path);
				const { tabs } = get();
				if (tabs.find((t) => t.id === id)) {
					set({ activeTabId: id });
					return id;
				}
				const newTab: GlobalTab = {
					...tab,
					id,
					moduleId: tab.moduleId ?? resolveModuleId(tab.path),
				};
				set({ tabs: [...tabs, newTab], activeTabId: id });
				return id;
			},

			setActiveTab: (id) => set({ activeTabId: id }),

			closeTab: (id) => {
				const { tabs, activeTabId, closedTabs } = get();
				const tab = tabs.find((t) => t.id === id);
				if (!tab || tab.pinned) return null;

				const idx = tabs.indexOf(tab);
				const remaining = tabs.filter((t) => t.id !== id);

				// Push to closed stack
				const { dirty: _, ...clean } = tab;
				const newClosed = [clean, ...closedTabs].slice(0, MAX_CLOSED);

				set({ tabs: remaining, closedTabs: newClosed });

				if (activeTabId === id) {
					const nextTab = remaining[Math.min(idx, remaining.length - 1)];
					set({ activeTabId: nextTab?.id ?? null });
					return nextTab?.path ?? "/";
				}
				return null;
			},

			closeOthers: (id) => {
				const { tabs, closedTabs } = get();
				const keep = tabs.filter((t) => t.id === id || t.pinned);
				const removed = tabs.filter((t) => t.id !== id && !t.pinned);
				const newClosed = [
					...removed.map(({ dirty: _, ...rest }) => rest),
					...closedTabs,
				].slice(0, MAX_CLOSED);
				set({ tabs: keep, closedTabs: newClosed, activeTabId: id });
			},

			closeRight: (id) => {
				const { tabs, closedTabs } = get();
				const idx = tabs.findIndex((t) => t.id === id);
				if (idx < 0) return;
				const keep = tabs.slice(0, idx + 1);
				const removed = tabs.slice(idx + 1).filter((t) => !t.pinned);
				const keepPinned = tabs.slice(idx + 1).filter((t) => t.pinned);
				const newClosed = [
					...removed.map(({ dirty: _, ...rest }) => rest),
					...closedTabs,
				].slice(0, MAX_CLOSED);
				set({ tabs: [...keep, ...keepPinned], closedTabs: newClosed });
			},

			reopenTab: () => {
				const { closedTabs, tabs } = get();
				if (closedTabs.length === 0) return null;
				const [tab, ...rest] = closedTabs;
				// Don't reopen if already open
				if (tabs.find((t) => t.id === tab.id)) {
					set({ closedTabs: rest, activeTabId: tab.id });
					return tab.path;
				}
				set({
					tabs: [...tabs, tab],
					closedTabs: rest,
					activeTabId: tab.id,
				});
				return tab.path;
			},

			reorderTabs: (fromIndex, toIndex) => {
				set((s) => {
					const tabs = [...s.tabs];
					const [moved] = tabs.splice(fromIndex, 1);
					tabs.splice(toIndex, 0, moved);
					return { tabs };
				});
			},

			setDirty: (id, dirty) => {
				set((s) => ({
					tabs: s.tabs.map((t) => (t.id === id ? { ...t, dirty } : t)),
				}));
			},

			setTitle: (id, title) => {
				set((s) => ({
					tabs: s.tabs.map((t) => (t.id === id ? { ...t, title } : t)),
				}));
			},

			togglePin: (id) => {
				set((s) => ({
					tabs: s.tabs.map((t) =>
						t.id === id ? { ...t, pinned: !t.pinned, dirty: false } : t,
					),
				}));
			},

			syncWithPath: (path, title) => {
				const id = tabId(path);
				const { tabs } = get();
				if (tabs.find((t) => t.id === id)) {
					set({ activeTabId: id });
				} else {
					let updated = [
						...tabs,
						{
							id,
							path,
							title,
							pinned: path === "/",
							moduleId: resolveModuleId(path),
						},
					];
					// Evict oldest non-pinned tabs when over limit
					while (updated.length > MAX_TABS) {
						const victim = updated.find((t) => !t.pinned && t.id !== id);
						if (!victim) break;
						updated = updated.filter((t) => t.id !== victim.id);
					}
					set({
						tabs: updated,
						activeTabId: id,
					});
				}
			},
		}),
		{
			name: "omnios-tabs",
			version: 3,
			partialize: (s) => ({
				tabs: s.tabs.map(({ dirty, ...rest }) => rest),
				activeTabId: s.activeTabId,
				closedTabs: s.closedTabs,
			}),
			migrate: (persisted: unknown) => {
				// v0→v2: remove orphan tabs + collapse hasMenuBar module sub-routes
				// into a single module root tab
				const state = persisted as {
					tabs: GlobalTab[];
					activeTabId: string | null;
					closedTabs: GlobalTab[];
				};
				const defs = getModuleDefs();
				// Modules with own Workspace layout — collapse sub-routes
				const workspaceRoots = [
					...defs.filter((m) => m.hasMenuBar).map((m) => m.prefix),
					"/diagnostics",
				];
				const menuBarPrefixes = workspaceRoots;

				const seen = new Set<string>();
				const tabs = (state.tabs ?? []).filter((t) => {
					if (t.pinned || t.path === "/") return true;
					const moduleId = resolveModuleId(t.path);
					if (!moduleId) return false;
					// For hasMenuBar modules, only keep the root prefix tab
					const menuPrefix = menuBarPrefixes.find((p) =>
						t.path.startsWith(p),
					);
					if (menuPrefix) {
						if (seen.has(menuPrefix)) return false;
						seen.add(menuPrefix);
						t.path = menuPrefix;
						t.id = menuPrefix;
						return true;
					}
					return true;
				});
				return { ...state, tabs, closedTabs: [] };
			},
		},
	),
);
